import { CategoryConfigurationDefinitionResponse, ConfigurationFieldType } from '@ydistri/api-sdk';
import { useMemo } from 'react';
import {
  checkRequirementsForSpecialConfiguration,
  specialConfigurationSelectOptions,
} from '../segmentPotentialDefinitionLib';
import { useGetCategoryConfigurationDefinitionsQuery } from '../../../../../apis/apiApplication';
import { useGetCalculationConfigurationQuery } from '../../../../../apis/apiGlobalConfiguration';
import { useCategoryConfiguration } from '../../../../../hooks/useCategoryConfiguration';
import { ROOT_CATEGORY_ID } from '../../../../../components/global/_constants';
import { useTemplateOrCalculationWithValue } from '../../../../../hooks/useTemplateOrCalculationWithValue';

interface SpecialOptionsBasedOnConfiguration {
  selectOptions: { id: string; label: string; value: string }[];
  definitions: CategoryConfigurationDefinitionResponse[];
  additionalConfigurations: CategoryConfigurationDefinitionResponse[];
}

const useSpecialOptionsBasedOnConfiguration = (
  fieldType: ConfigurationFieldType,
): SpecialOptionsBasedOnConfiguration => {
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const { data: calculationConfiguration } = useGetCalculationConfigurationQuery(
    templateOrCalculation.value,
  );
  const { data: configurationDefinitions } = useGetCategoryConfigurationDefinitionsQuery();

  const { rootCategoryConfiguration } = useCategoryConfiguration(ROOT_CATEGORY_ID);

  return useMemo(() => {
    if (!configurationDefinitions || !calculationConfiguration)
      return {
        selectOptions: [],
        definitions: [],
        additionalConfigurations: [],
      };
    const relevantDefinitions = configurationDefinitions.filter(cd =>
      specialConfigurationSelectOptions[fieldType]?.selectOptions.includes(cd.fieldType),
    );

    return {
      selectOptions: relevantDefinitions
        .filter(cd => !calculationConfiguration.skuListConfigurationFlagIds?.[cd.fieldType])
        .map(rd => ({
          id: rd.fieldType,
          label: rd.title,
          value: rd.fieldType,
        })),
      definitions: relevantDefinitions.filter(
        cd => calculationConfiguration.skuListConfigurationFlagIds?.[cd.fieldType],
      ),
      additionalConfigurations: configurationDefinitions.filter(
        cd =>
          specialConfigurationSelectOptions[fieldType]?.additionalConfigurations.includes(
            cd.fieldType,
          ) &&
          checkRequirementsForSpecialConfiguration(
            cd.fieldType,
            rootCategoryConfiguration,
            !!calculationConfiguration.ptRequestListEntityList,
          ),
      ),
    };
  }, [configurationDefinitions, calculationConfiguration, fieldType, rootCategoryConfiguration]);
};

export default useSpecialOptionsBasedOnConfiguration;
